import { ThemeComponent } from "@/theme/ThemeComponent";
import {  Component, Prop, Ref } from "@eastsideco/salvo-ts";

/**
 * ----------------------------------------------------------------------------
 * Component: Reveal Section
 * - Creates reveal-style layouts with images, title, and copy
 * - Similar to hero section but adapted for reveal layouts
 * - Supports both click and auto modes based on reveal_content_on_click setting
 * - When only final content is present, slides covers at 20% viewport visibility
 * ------------------------------------------------------------------------------
 */
@Component
export class RevealSection extends ThemeComponent {

    @Ref({ required: false }) initialContent!: HTMLElement;
    @Ref({ required: false }) revealContent!: HTMLElement;
    @Ref({ required: false }) initialTitle!: HTMLElement;
    @Ref({ required: false }) revealContentButton!: HTMLElement;
    @Ref({ required: false }) revealTitle!: HTMLElement;
    @Ref({ required: false }) imageContainer!: HTMLElement;
    @Ref({ required: false }) finalContent!: HTMLElement;
    @Ref({ required: false }) leftCover!: HTMLElement;
    @Ref({ required: false }) rightCover!: HTMLElement;

    @Prop({ default: false }) revealContentOnClick!: boolean;

    private currentState: 'initial' | 'reveal' | 'final' = 'initial';
    private hasAnimated: boolean = false;
    private observer: IntersectionObserver | null = null;
    private isFinalOnlyMode: boolean = false;

    init() {
        this.detectFinalOnlyMode();
        this.setInitialState();

        if (this.isFinalOnlyMode) {
            this.setupFinalOnlyAnimation();
        } else if (this.revealContentOnClick) {
            this.setupClickListeners();
        } else {
            this.setupAutoAnimation();
        }
    }

    private detectFinalOnlyMode() {
        // Check if both initial and reveal titles are empty/not present
        const hasInitialTitle = this.initialTitle;
        const hasRevealTitle = this.revealTitle;

        this.isFinalOnlyMode = !hasInitialTitle && !hasRevealTitle;
    }

    private setupFinalOnlyAnimation() {
        // Create intersection observer to detect when 20% of section is visible
        this.observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && !this.hasAnimated) {
                        this.hasAnimated = true;
                        this.animateCoverElements();
                    }
                }
            },
            {
                threshold: 0.2, // Trigger when 20% visible
                rootMargin: '0px'
            }
        );

        // Observe the main reveal section element
        const hostElement = this as unknown as HTMLElement;
        if (hostElement && this.observer) {
            this.observer.observe(hostElement);
        }
    }

    private setupAutoAnimation() {
        // Create intersection observer to detect when section is mostly visible
        this.observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting && !this.hasAnimated) {
                        this.hasAnimated = true;
                        this.startAnimationSequence();
                    }
                }
            },
            {
                threshold: 0.6, // Trigger when 60% visible to be more reliable
                rootMargin: '0px'
            }
        );

        // Observe the main reveal section element
        if (this) {
            this.observer.observe(this as unknown as HTMLElement);
        }
    }

    private setupClickListeners() {
        if (this.initialContent) {
            this.initialContent.addEventListener('click', () => this.startAnimationSequence());
        }
    }

    private startAnimationSequence() {
        // Start the animation sequence with delays
        setTimeout(() => this.transitionToReveal(), 1000);
        setTimeout(() => this.transitionToFinal(), 4000);
    }

    private setInitialState() {
        if (this.isFinalOnlyMode) {
            // Hide initial and reveal content in final-only mode
            if (this.initialContent) {
                this.initialContent.style.display = 'none';
            }
            if (this.revealContent) {
                this.revealContent.style.display = 'none';
            }
            return;
        }

        if (this.initialContent) {
            // Set pointer events based on mode
            this.initialContent.style.pointerEvents = this.revealContentOnClick ? 'auto' : 'none';
        }

        if (this.initialTitle) {
            this.initialTitle.style.opacity = '1';
        }
        if (this.revealContentButton) {
            this.revealContentButton.style.opacity = '1';
        }

        if (this.revealContent) {
            // Set pointer events based on mode
            this.revealContent.style.pointerEvents = 'none'; // Will be set to 'auto' when needed for click mode
        }

        if (this.revealTitle) {
            this.revealTitle.style.opacity = '0'; // Hide title initially
        }

        // Ensure final content is hidden initially
        if (this.finalContent) {
            this.finalContent.classList.remove('reveal__content--visible');
        }
    }

    private transitionToReveal() {
        if (this.currentState !== 'initial') {return;}

        this.currentState = 'reveal';

        // Hide initial title
        if (this.initialTitle) {
            this.initialTitle.style.transition = 'opacity 0.5s ease-in-out';
            this.initialTitle.style.opacity = '0';
        }

        if (this.revealContentButton) {
            this.revealContentButton.style.transition = 'opacity 0.5s ease-in-out';
            this.revealContentButton.style.opacity = '0';
        }

        // Update pointer events for click mode
        if (this.revealContentOnClick && this.initialContent) {
            this.initialContent.style.pointerEvents = 'none';
        }

        // Show reveal content background and slowly fade in the reveal title
        if (this.revealContent) {
            this.revealContent.style.transition = 'opacity 0.5s ease-in-out';
            this.revealContent.style.opacity = '1';

            // Enable pointer events for click mode
            if (this.revealContentOnClick) {
                this.revealContent.style.pointerEvents = 'auto';
            }
        }

        // Slowly fade in the reveal title (background stays white)
        if (this.revealTitle) {
            setTimeout(() => {
                if (this.revealTitle) {
                    this.revealTitle.style.transition = 'opacity 1s ease-in-out';
                    this.revealTitle.style.opacity = '1';
                }
            }, 500);
        }
    }

    private transitionToFinal() {
        if (this.currentState !== 'reveal') {return;}

        this.currentState = 'final';

        // Hide reveal title
        if (this.revealTitle) {
            this.revealTitle.style.transition = 'opacity 1s ease-in-out';
            this.revealTitle.style.opacity = '0';
        }

        // Update pointer events for click mode
        if (this.revealContentOnClick && this.revealContent) {
            this.revealContent.style.pointerEvents = 'none';
        }

        // Hide reveal content
        if (this.revealContent) {
            this.revealContent.style.transition = 'opacity 1s ease-in-out';
            this.revealContent.style.opacity = '0';
        }
        setTimeout(() => {
            // Trigger the sliding animation for the cover elements
            this.animateCoverElements();
        }, 1000);
    }

    private animateCoverElements() {
        if (this.leftCover && this.rightCover) {
            // Add CSS classes to trigger the sliding animation
            this.leftCover.classList.add('reveal__image-container--sliding');
            this.rightCover.classList.add('reveal__image-container--sliding');

            // Show final content after the sliding animation starts
            setTimeout(() => {
                if (this.finalContent) {
                    this.finalContent.classList.add('reveal__content--visible');
                }
            }, 500); // Halfway through the sliding animation
        }
    }

    destroy() {
        // Clean up observer when component is destroyed
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}
